import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Button,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import {
  RiskScoreCard,
  CreditProfileSnippet,
  HealthStatusGrid,
  BusinessPerformance,
  BoundaryConditions,
  CreditLineSimulation,
  CreditDecision,
} from '@/components/snippets'
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileText,
  TrendingUp,
  Shield,
  Loader2,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { memosApi } from '@/services/api'
import { MemoDetail } from '@/types'

// CSS for beautifully styled memo HTML content
const memoStyles = `
  .memo-content {
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.7;
    color: #1e293b;
  }
  
  .memo-content h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 3px solid #0ea5e9;
    padding-bottom: 0.75rem;
    margin-bottom: 1.5rem;
    margin-top: 2rem;
  }
  
  .memo-content h1:first-child {
    margin-top: 0;
    text-align: center;
    border-bottom: none;
    background: linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .memo-content h2 {
    font-size: 1.35rem;
    font-weight: 600;
    color: #1e3a8a;
    border-left: 4px solid #0ea5e9;
    padding-left: 1rem;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  .memo-content h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #334155;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  
  .memo-content p {
    margin-bottom: 1rem;
    color: #475569;
  }
  
  .memo-content strong {
    color: #0f172a;
    font-weight: 600;
  }
  
  /* Tables */
  .memo-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  .memo-content thead {
    background: linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%);
  }
  
  .memo-content th {
    padding: 0.875rem 1rem;
    text-align: left;
    font-weight: 600;
    color: white;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .memo-content tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: background-color 0.15s;
  }
  
  .memo-content tbody tr:hover {
    background-color: #f8fafc;
  }
  
  .memo-content tbody tr:nth-child(even) {
    background-color: #f1f5f9;
  }
  
  .memo-content td {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
  
  /* Lists */
  .memo-content ul, .memo-content ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  .memo-content li {
    margin-bottom: 0.5rem;
    color: #475569;
  }
  
  .memo-content li::marker {
    color: #0ea5e9;
  }
  
  /* Horizontal rules */
  .memo-content hr {
    border: none;
    height: 2px;
    background: linear-gradient(90deg, #0ea5e9 0%, #7c3aed 50%, #0ea5e9 100%);
    margin: 2rem 0;
    border-radius: 1px;
  }
  
  /* Blockquotes - for pre-check scores and notes */
  .memo-content blockquote {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-left: 4px solid #f59e0b;
    padding: 1rem 1.5rem;
    margin: 1.5rem 0;
    border-radius: 0 8px 8px 0;
    font-style: italic;
  }
  
  .memo-content blockquote strong {
    color: #92400e;
  }
  
  /* Images */
  .memo-content img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
    margin: 1.5rem auto;
    display: block;
  }
`

// Check if memo content is valid and complete
function isMemoComplete(content: string): { valid: boolean; reason?: string } {
  if (!content || content.trim().length < 100) {
    return { valid: false, reason: 'Memo content is empty or too short' }
  }
  
  // Check for key markers that indicate a properly generated memo
  const hasRiskAssessment = /Risk Assessment|Risk Rating|Risk Score/i.test(content)
  const hasRecommendation = /Recommendation|Credit Decision/i.test(content)
  const hasAnalysis = /Analysis|Assessment|Summary/i.test(content)
  const hasQuickStats = /Quick Statistics|Metric.*\|.*Value/i.test(content)
  
  // Must have at least risk assessment and some analysis
  if (!hasRiskAssessment && !hasRecommendation) {
    return { valid: false, reason: 'Missing risk assessment or recommendation' }
  }
  
  if (!hasAnalysis && !hasQuickStats) {
    return { valid: false, reason: 'Missing analysis content' }
  }
  
  return { valid: true }
}

// Parse data from memo content for the snippet cards
function parseMemoData(content: string, _memoId: string) {
  // First validate the memo is complete
  const validation = isMemoComplete(content)
  if (!validation.valid) {
    return null // Return null for incomplete memos
  }
  
  // Extract key information from the memo header
  const customerMatch = content.match(/\*\*Customer:\*\*\s*(.+?)(?:\s*\\n|\n|$)/i) || 
                        content.match(/Entity:\s*([^\n(]+)/i) ||
                        content.match(/\*\*RE:\*\*.*?-\s*([^(]+)/i)  // BRCS format: **RE:** Credit Risk Assessment - COMPANY NAME
  const generatedMatch = content.match(/\*\*Generated:\*\*\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                         content.match(/\*\*DATE:\*\*\s*(.+?)(?:\s*\\n|\n|$)/i)
  const riskMatch = content.match(/\*\*Risk Rating:\*\*\s*(.+?)(?:\s*\\n|\n|$)/i) ||  // BRCS format
                    content.match(/Risk Agent Assessment:\*\*?\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                    content.match(/Overall Risk Rating:\*\*?\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                    content.match(/Risk Rating:\s*(.+?)(?:\s*\\n|\n|$)/i)
  const recommendationMatch = content.match(/\*\*Recommendation:\*\*\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                              content.match(/Risk Agent Recommendation:\*\*?\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                              content.match(/Credit Recommendation:\*\*?\s*(.+?)(?:\s*\\n|\n|$)/i) ||
                              content.match(/Credit Committee Recommendation[:\s]*\n+\*\*\d+\.\s*(\w+)/i)  // "1. APPROVE IN PRINCIPLE"
  
  // Extract from Quick Statistics table (most accurate source)
  const requestedLoanMatch = content.match(/Requested Loan\s*\|\s*\$?([\d,]+(?:\.\d{2})?)/i)
  const annualRevMatch = content.match(/Annual Revenue\s*\|\s*\$?([\d,]+(?:\.\d{2})?)/i)
  const monthlyAvgMatch = content.match(/Monthly Average\s*\|\s*\$?([\d,]+(?:\.\d{2})?)/i)
  const settlementRateMatch = content.match(/Settlement Success Rate\s*\|\s*(\d+(?:\.\d+)?%?)/i)
  const refundRateMatch = content.match(/Refund Rate\s*\|\s*(\d+(?:\.\d+)?%?)/i)
  const totalProductsMatch = content.match(/Total Products\s*\|\s*(\d+)/i)
  const accountHealthMatch = content.match(/Account Health\s*\|\s*(\w+(?:\s+\w+)?)/i)
  
  const parseNumber = (val: string | undefined) => {
    if (!val) return 0
    return parseFloat(val.replace(/,/g, '').replace('%', ''))
  }
  
  const riskLevel = riskMatch?.[1]?.trim() || 'Unknown'
  const recommendation = recommendationMatch?.[1]?.trim() || 'Pending'
  const accountHealth = accountHealthMatch?.[1]?.trim() || 'Unknown'
  
  // Calculate risk score on 10-point scale (1=low risk, 10=high risk)
  // This matches the backend's scoring system
  let riskScore: number
  
  // PRIORITY 1: Try to extract numeric risk score first (from our template requirement)
  // Look for exact format: **Risk Score: X/10**
  const numericScoreMatch = content.match(/\*\*Risk Score:\s*(\d+(?:\.\d+)?)\s*\/\s*10\*\*/i)
  if (numericScoreMatch) {
    riskScore = parseFloat(numericScoreMatch[1])
  } else {
    // Fallback patterns for numeric score
    const fallbackScoreMatch = content.match(/Risk Score[:\s]*(\d+(?:\.\d+)?)\s*\/\s*10/i) ||
                               content.match(/Risk Score[:\s]*(\d+(?:\.\d+)?)\s+out of\s+10/i) ||
                               content.match(/Risk Score[:\s]*(\d+(?:\.\d+)?)/i)
    if (fallbackScoreMatch) {
      const score = parseFloat(fallbackScoreMatch[1])
      // If score > 10, might be old format, clamp to 10
      riskScore = Math.min(score, 10)
    } else {
      // PRIORITY 2: Derive from LLM Risk Assessment text (same logic as backend)
      const riskLevelLower = riskLevel.toLowerCase()
      if (riskLevelLower.includes('critical') || riskLevelLower.includes('very high')) {
        riskScore = 9  // Very high/critical risk
      } else if (riskLevelLower.includes('high')) {
        riskScore = 7  // High risk
      } else if (riskLevelLower.includes('medium') || riskLevelLower.includes('moderate')) {
        riskScore = 5  // Medium risk
      } else if (riskLevelLower.includes('low')) {
        riskScore = 3  // Low risk
      } else {
        riskScore = 5  // Default to medium
      }
    }
  }
  
  // Also extract risk category from our template format
  const riskCategoryMatch = content.match(/\*\*Risk Category:\s*([^\*\n]+)\*\*/i)
  const riskCategory = riskCategoryMatch?.[1]?.trim() || riskLevel
  
  // Determine approval status
  let approvalStatus: 'approved' | 'conditional' | 'declined' = 'conditional'
  const recLower = recommendation.toLowerCase()
  if (recLower.includes('decline') || recLower.includes('reject')) {
    approvalStatus = 'declined'
  } else if (recLower.includes('approve') && !recLower.includes('conditional')) {
    approvalStatus = 'approved'
  }
  
  const annualRevenue = parseNumber(annualRevMatch?.[1])
  const monthlyAverage = parseNumber(monthlyAvgMatch?.[1])
  const requestedLoan = parseNumber(requestedLoanMatch?.[1]) || 10000
  const settlementRate = parseNumber(settlementRateMatch?.[1])
  const refundRate = parseNumber(refundRateMatch?.[1])
  const totalProducts = parseNumber(totalProductsMatch?.[1])
  
  // Format values for display
  const formatPercent = (val: number) => val > 0 ? `${val}%` : '-'
  
  // Determine confidence based on risk score (higher risk = lower confidence)
  const confidence = riskScore <= 4 ? 'high' as const : riskScore <= 6 ? 'medium' as const : 'low' as const
  
  return {
    customerName: customerMatch?.[1]?.trim() || 'Customer',
    generated: generatedMatch?.[1]?.trim() || new Date().toLocaleString(),
    riskLevel: riskCategory, // Use the better parsed category
    recommendation,
    riskScore,
    approvalStatus,
    // Raw values for calculations
    annualRevenue,
    monthlyAverage,
    requestedLoan,
    settlementRate,
    refundRate,
    totalProducts,
    accountHealth,
    creditProfile: {
      customerName: customerMatch?.[1]?.trim() || 'Customer',
      country: 'UNITED STATES OF AMERICA',
      timestamp: generatedMatch?.[1]?.trim() || new Date().toISOString().split('T')[0],
      finalScore: riskScore,
      riskCategory: riskCategory, // Use the better parsed category
      customerSegment: annualRevenue > 500000 ? 'Enterprise' : annualRevenue > 100000 ? 'SMB' : 'Micro',
      creditLimit: approvalStatus === 'declined' ? 0 : requestedLoan,
      shadowLimit: approvalStatus === 'declined' ? 0 : Math.round(requestedLoan * 0.1),
      maxTicketSize: requestedLoan * 2,
    },
    creditDecision: {
      recommendedLimit: approvalStatus === 'declined' ? 0 : requestedLoan,
      maxLimit: requestedLoan * 2,
      riskScore,
      riskCategory: riskCategory, // Use the better parsed category
      confidence,
      approvalStatus,
      conditions: approvalStatus === 'declined' 
        ? ['Application declined due to high risk profile']
        : [
            'Maintain consistent sales performance',
            'Regular account review required',
            'Quarterly performance monitoring',
          ],
    },
    businessPerformance: {
      annualNetSales: annualRevenue,
      latestQuarterlyAvgNetSales: monthlyAverage * 3 || annualRevenue / 4,
      latest6MonthsAvgNetSales: monthlyAverage || annualRevenue / 12,
      recentSalesGrowth: 0,
    },
    healthStatus: [
      { 
        id: 1, 
        name: 'Account Health', 
        value: accountHealth, 
        status: (accountHealth.toLowerCase().includes('risk') ? 'AT_RISK' : accountHealth.toLowerCase() === 'good' || accountHealth.toLowerCase() === 'great' ? 'GREAT' : 'GOOD') as 'GREAT' | 'GOOD' | 'FAIR' | 'POOR' | 'NONE' | 'AT_RISK',
        description: 'Overall seller account health score' 
      },
      { 
        id: 2, 
        name: 'Settlement Rate', 
        value: formatPercent(settlementRate), 
        status: (settlementRate > 95 ? 'GREAT' : settlementRate > 90 ? 'GOOD' : settlementRate > 0 ? 'FAIR' : 'NONE') as 'GREAT' | 'GOOD' | 'FAIR' | 'POOR' | 'NONE' | 'AT_RISK',
        description: 'Percentage of successful settlements' 
      },
      { 
        id: 3, 
        name: 'Refund Rate', 
        value: formatPercent(refundRate), 
        status: (refundRate > 0 && refundRate < 3 ? 'GREAT' : refundRate < 5 ? 'GOOD' : refundRate > 0 ? 'AT_RISK' : 'NONE') as 'GREAT' | 'GOOD' | 'FAIR' | 'POOR' | 'NONE' | 'AT_RISK',
        description: 'Percentage of orders refunded' 
      },
      { 
        id: 4, 
        name: 'Total Products', 
        value: totalProducts > 0 ? totalProducts.toString() : '-', 
        status: (totalProducts > 20 ? 'GREAT' : totalProducts > 10 ? 'GOOD' : totalProducts > 0 ? 'FAIR' : 'NONE') as 'GREAT' | 'GOOD' | 'FAIR' | 'POOR' | 'NONE' | 'AT_RISK',
        description: 'Number of active products' 
      },
    ],
    boundaryConditions: [
      { 
        description: 'Account Health Status', 
        value: accountHealth, 
        status: (accountHealth.toLowerCase().includes('risk') ? 'AT_RISK' : 'GOOD') as 'GOOD' | 'AT_RISK'
      },
      { 
        description: 'Settlement Success Rate > 90%', 
        value: formatPercent(settlementRate), 
        status: (settlementRate > 90 ? 'GOOD' : 'AT_RISK') as 'GOOD' | 'AT_RISK'
      },
      { 
        description: 'Refund Rate < 5%', 
        value: formatPercent(refundRate), 
        status: (refundRate > 0 && refundRate < 5 ? 'GOOD' : 'AT_RISK') as 'GOOD' | 'AT_RISK'
      },
      { 
        description: 'Credit Recommendation', 
        value: recommendation, 
        status: (approvalStatus === 'declined' ? 'AT_RISK' : 'GOOD') as 'GOOD' | 'AT_RISK'
      },
    ],
    creditLineSimulation: [
      { description: 'Requested Loan Amount', value: requestedLoan },
      { description: 'Annual Revenue', value: Math.round(annualRevenue) },
      { description: 'Monthly Average Revenue', value: Math.round(monthlyAverage) },
      { description: 'Recommended Credit Limit', value: approvalStatus === 'declined' ? 0 : requestedLoan },
    ],
  }
}

// Type for non-null parsed data
type ParsedMemoData = NonNullable<ReturnType<typeof parseMemoData>>

// Styled memo page component
function StyledMemoContent({ memo, parsedData }: { memo: MemoDetail; parsedData: ParsedMemoData }) {
  const { approvalStatus, riskLevel, recommendation, creditProfile, creditDecision } = parsedData
  
  // Get badge colors
  const getRiskColor = () => {
    if (riskLevel.toLowerCase().includes('very high') || riskLevel.toLowerCase().includes('critical')) return 'bg-red-600'
    if (riskLevel.toLowerCase().includes('high')) return 'bg-red-500'
    if (riskLevel.toLowerCase().includes('medium')) return 'bg-amber-500'
    return 'bg-green-500'
  }
  
  const getApprovalColor = () => {
    if (approvalStatus === 'declined') return 'bg-red-600'
    if (approvalStatus === 'approved') return 'bg-green-600'
    return 'bg-amber-500'
  }
  
  const ApprovalIcon = approvalStatus === 'declined' ? XCircle : approvalStatus === 'approved' ? CheckCircle : AlertCircle

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="text-center border-b pb-6 mb-6">
        <h1 className="text-2xl font-bold text-dashboard-navy">
          {memo.title || 'Credit Analysis Memo'}
        </h1>
        <p className="text-gray-500 mt-1">Generated: {parsedData.generated}</p>
        <div className="flex justify-center gap-3 mt-4">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium text-white ${getRiskColor()}`}>
            {riskLevel}
          </span>
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium text-white flex items-center gap-1.5 ${getApprovalColor()}`}>
            <ApprovalIcon className="h-4 w-4" />
            {recommendation}
          </span>
        </div>
      </div>

      {/* Executive Summary */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold text-dashboard-navy border-b pb-2">
          Executive Summary
        </h2>
        
        <div className={`p-6 rounded-lg text-white ${
          approvalStatus === 'declined' 
            ? 'bg-gradient-to-r from-red-700 to-red-500' 
            : approvalStatus === 'approved'
              ? 'bg-gradient-to-r from-green-700 to-green-500'
              : 'bg-gradient-to-r from-dashboard-navy to-dashboard-purple'
        }`}>
          <div className="text-center">
            <div className="text-sm opacity-80 mb-1">Credit Decision</div>
            <div className="text-4xl font-bold">
              {approvalStatus === 'declined' ? 'DECLINE' : `$${creditDecision.recommendedLimit.toLocaleString()}`}
            </div>
            <div className="text-sm opacity-80 mt-2">
              {approvalStatus === 'declined' ? 'Application not approved' : `${approvalStatus.toUpperCase()} approval`}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="text-xs text-gray-500 uppercase">Customer</span>
            <p className="font-bold text-lg">{creditProfile.customerName}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase">Risk Score</span>
            <p className={`font-bold text-lg ${creditProfile.finalScore < 2.5 ? 'text-red-600' : creditProfile.finalScore < 3.5 ? 'text-amber-600' : 'text-green-600'}`}>
              {creditProfile.finalScore.toFixed(1)} / 10.00
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase">Segment</span>
            <p className="font-bold text-lg">{creditProfile.customerSegment}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase">Memo Type</span>
            <p className="font-bold text-lg">{memo.type?.toUpperCase() || 'Analysis'}</p>
          </div>
        </div>
      </section>

      {/* Full Memo Content */}
      <section className="space-y-4 mt-8">
        <h2 className="text-xl font-bold text-dashboard-navy border-b pb-2">
          Detailed Analysis
        </h2>
        <div 
          className="memo-content prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: memo.htmlContent || '' }}
        />
      </section>

      <div className="text-xs text-gray-400 text-center mt-8 pt-4 border-t">
        This memo was generated automatically by the Credit Analysis System.
        All recommendations are subject to final review.
      </div>
    </div>
  )
}

export function MemoViewer() {
  const { memoId } = useParams()
  const [zoom, setZoom] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [memo, setMemo] = useState<MemoDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch memo data
  useEffect(() => {
    if (memoId) {
      setLoading(true)
      setError(null)
      memosApi.get(memoId)
        .then((data) => {
          setMemo(data)
        })
        .catch((err) => {
          console.error('Failed to fetch memo:', err)
          setError('Failed to load memo')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [memoId])

  // Parse memo data for snippet cards
  const parsedData = useMemo(() => {
    if (!memo?.content) return null
    return parseMemoData(memo.content, memoId || '')
  }, [memo, memoId])

  // Single page view - no duplicate tabs
  const totalPages = 1

  return (
    <div className="h-full flex flex-col -m-6">
      {/* Inject custom styles */}
      <style>{memoStyles}</style>
      
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-lg font-bold text-dashboard-navy">
              {memo?.title || 'Credit Analysis Memo'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {memo?.customerId || 'Loading...'} - {memo?.type === 'amazon' ? 'Amazon Seller' : memo?.type === 'combined' ? 'Combined Credit' : memo?.type?.toUpperCase()} Analysis
            </p>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - PDF Viewer */}
        <div className="w-1/2 border-r flex flex-col bg-gray-100">
          {/* PDF Controls */}
          <div className="p-2 border-b bg-white flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2 min-w-[100px] text-center">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setZoom(Math.max(50, zoom - 10))}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2 min-w-[50px] text-center">{zoom}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setZoom(Math.min(200, zoom + 10))}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* PDF Content */}
          <ScrollArea className="flex-1">
            <div
              id="memo-content"
              className="bg-white shadow-lg m-4 p-8 rounded-lg"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-dashboard-navy" />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-20 text-red-600">
                  <AlertCircle className="h-12 w-12 mb-4" />
                  <p className="text-lg font-medium">{error}</p>
                </div>
              ) : memo && !parsedData ? (
                // Memo exists but parsing failed - incomplete/invalid memo
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="text-center">
                    <div className="mb-4">
                      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                        <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-dashboard-navy mb-2">Memo Generation In Progress</h3>
                    <p className="text-gray-600 mb-4 max-w-md">
                      The credit analysis is still being generated. This typically takes 30-60 seconds.
                    </p>
                    <p className="text-sm text-gray-500">
                      Please wait or check back in a moment.
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-6"
                      onClick={() => window.location.reload()}
                    >
                      <Loader2 className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>
              ) : memo && parsedData ? (
                <StyledMemoContent memo={memo} parsedData={parsedData} />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                  <FileText className="h-12 w-12 mb-4" />
                  <p>No content available</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Snippet Cards */}
        <div className="w-1/2 flex flex-col bg-gray-50">
          <div className="p-3 border-b bg-white shadow-sm">
            <h3 className="text-sm font-semibold text-dashboard-navy">Analysis Snippets</h3>
            <p className="text-xs text-gray-500">Interactive visual summary of the credit analysis</p>
          </div>
          <ScrollArea className="flex-1 p-4">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-dashboard-navy" />
              </div>
            ) : !parsedData ? (
              // No valid parsed data - show pending message on snippets side too
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <Loader2 className="h-10 w-10 mb-4 animate-spin text-amber-500" />
                <p className="font-medium">Generating Analysis...</p>
                <p className="text-sm mt-2">Snippets will appear once the analysis is complete</p>
              </div>
            ) : (
              <Tabs defaultValue="decision" className="space-y-4">
                <TabsList className="w-full grid grid-cols-4 h-auto p-1">
                  <TabsTrigger value="decision" className="text-xs py-2">
                    <Shield className="h-3 w-3 mr-1" />
                    Decision
                  </TabsTrigger>
                  <TabsTrigger value="overview" className="text-xs py-2">
                    <FileText className="h-3 w-3 mr-1" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="metrics" className="text-xs py-2">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Metrics
                  </TabsTrigger>
                  <TabsTrigger value="risk" className="text-xs py-2">
                    Risk
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="decision" className="space-y-4">
                  <CreditDecision {...parsedData.creditDecision} />
                  <CreditProfileSnippet {...parsedData.creditProfile} />
                </TabsContent>

                <TabsContent value="overview" className="space-y-4">
                  <CreditProfileSnippet {...parsedData.creditProfile} />
                  <div className="flex items-center justify-center py-4">
                    <RiskScoreCard
                      score={parsedData.creditProfile.finalScore}
                      maxScore={10}
                      riskCategory={parsedData.creditProfile.riskCategory}
                      title="Risk Score"
                      className="w-full max-w-xs"
                    />
                  </div>
                  <BusinessPerformance data={parsedData.businessPerformance} />
                </TabsContent>

                <TabsContent value="metrics" className="space-y-4">
                  <HealthStatusGrid items={parsedData.healthStatus} columns={2} />
                </TabsContent>

                <TabsContent value="risk" className="space-y-4">
                  <BoundaryConditions conditions={parsedData.boundaryConditions} />
                  <CreditLineSimulation items={parsedData.creditLineSimulation} />
                </TabsContent>
              </Tabs>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Hidden container for PDF export - just the memo content */}
      <div
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          width: '700px',  // Narrower to better fit A4 proportions
          zIndex: -1,
        }}
        aria-hidden="true"
      >
        {memo && (
          <div 
            id="memo-export-page-1" 
            className="bg-white p-6 memo-content prose prose-sm max-w-none" 
            style={{ 
              backgroundColor: '#ffffff',
              fontSize: '11px',  // Smaller font to fit more content per page
              lineHeight: '1.5',
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: memo.htmlContent || '' }} />
          </div>
        )}
      </div>
    </div>
  )
}
